import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal, WritableSignal } from '@angular/core';
import { OrIconComponent, PageActionsComponent, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { IonicModule } from '@ionic/angular';
import { NgClass } from '@angular/common';
import { availableLevels, gameLevelMapper, SelectedLocation, weekdayTranslation } from '@core';
import {
  GetAlertsSettingsQueryHandler,
  GetAlertsSettingsQueryResult,
  UpdateAlertsSettingsCommandHandler
} from '../../application/handlers';
import { AlertTime } from '../../application/interfaces';
import { GameLevelEnum, Weekday } from '@core-http';
import { AlertSettingsViewService } from './view-service/alert-settings.view-service';

@Component({
  selector: 'lib-notifications-settings',
  templateUrl: './alert-settings.component.html',
  styleUrls: ['./alert-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TitileNavbarComponent, IonicModule, NgClass, PageLayoutComponent, PageActionsComponent, OrIconComponent],
  providers: [GetAlertsSettingsQueryHandler, UpdateAlertsSettingsCommandHandler, AlertSettingsViewService]
})
export class AlertSettingsComponent implements OnInit {
  private readonly viewService: AlertSettingsViewService = inject(AlertSettingsViewService);

  readonly gameLevels: GameLevelEnum[] = availableLevels;
  readonly gameLevelMapper: Record<GameLevelEnum, string> = gameLevelMapper;
  readonly weekdaysMapper: Record<Weekday, string> = weekdayTranslation;
  readonly weekDays: Weekday[] = [
    Weekday.MONDAY,
    Weekday.TUESDAY,
    Weekday.WEDNESDAY,
    Weekday.THURSDAY,
    Weekday.FRIDAY,
    Weekday.SATURDAY,
    Weekday.SUNDAY
  ];

  readonly notificationsValues: WritableSignal<GetAlertsSettingsQueryResult> = this.viewService.notificationsValues;
  readonly isDirty: Signal<boolean> = this.viewService.isDirty;
  readonly canSave: Signal<boolean> = this.viewService.canSave;
  readonly selectedLocation: Signal<SelectedLocation | null> = this.viewService.selectedLocation;

  readonly isSelectAllWeekdaysVisible: Signal<boolean> = computed(() => {
    return this.notificationsValues().specificWeekdays?.length !== 7;
  });

  readonly isAllDay: Signal<boolean> = computed(() => {
    const time: AlertTime = this.notificationsValues().time;
    return time.hoursMin === 0 && time.hoursMax === 24;
  });

  private readonly minHoursGap: number = 1;
  private readonly defaultTime: AlertTime = { hoursMin: 8, hoursMax: 22 };
  private previousTime: AlertTime | null = null;

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    this.viewService.loadSettings();
  }

  cancel(): void {
    this.viewService.cancel();
  }

  saveSettings(): void {
    this.viewService.saveSettings();
  }

  toggleNotifications(event: CustomEvent): void {
    this.viewService.toggleNotifications(event);
  }

  openLocationPicker(): void {
    this.viewService.openLocationPicker();
  }

  clearLocation(): void {
    this.viewService.clearLocation();
  }

  openDistance(): void {
    this.viewService.openDistance();
  }

  distanceLabel(maxDistanceMeters: number): string {
    return `+ ${maxDistanceMeters / 1000} km`;
  }

  onTimeChange(event: CustomEvent): void {
    const { lower, upper }: { lower: number; upper: number } = event.detail.value;
    const current: AlertTime = this.notificationsValues().time;

    let hoursMin: number = lower;
    let hoursMax: number = upper;

    // Wymuszamy co najmniej 1h odstępu - dosuwamy gałkę, której użytkownik nie przeciągał.
    if (hoursMax - hoursMin < this.minHoursGap) {
      if (lower !== current.hoursMin) {
        hoursMin = Math.min(lower, 24 - this.minHoursGap);
        hoursMax = hoursMin + this.minHoursGap;
      } else {
        hoursMax = Math.max(upper, this.minHoursGap);
        hoursMin = hoursMax - this.minHoursGap;
      }
    }

    this.setTime({ hoursMin, hoursMax });
  }

  toggleAllDay(event: CustomEvent): void {
    const isAllDay: boolean = event.detail.checked;

    if (isAllDay) {
      this.previousTime = this.notificationsValues().time;
      this.setTime({ hoursMin: 0, hoursMax: 24 });
    } else {
      this.setTime(this.previousTime ?? this.defaultTime);
    }
  }

  private setTime(time: AlertTime): void {
    this.notificationsValues.update((values: GetAlertsSettingsQueryResult) => ({
      ...values,
      time
    }));
  }

  levelChips(level: GameLevelEnum): boolean {
    return this.notificationsValues().gameLevel?.some((gameLevel: GameLevelEnum) => gameLevel === level);
  }

  isLevelChecked(value: GameLevelEnum): void {
    const { gameLevel, ...rest } = this.notificationsValues();

    const updatedGameLevel: GameLevelEnum[] = gameLevel.includes(value)
      ? gameLevel.filter((v: GameLevelEnum) => v !== value)
      : [...gameLevel, value];

    this.notificationsValues.set({
      ...rest,
      gameLevel: updatedGameLevel
    });
  }

  selectWeekday(value: Weekday): void {
    const { specificWeekdays, ...rest } = this.notificationsValues();

    const updatedWeekdays: Weekday[] = specificWeekdays?.includes(value)
      ? specificWeekdays.filter((v: Weekday) => v !== value)
      : [...specificWeekdays, value];

    this.notificationsValues.set({
      ...rest,
      specificWeekdays: updatedWeekdays
    });
  }

  isDayChecked(day: Weekday): boolean {
    return this.notificationsValues().specificWeekdays.some((v: Weekday) => v === day);
  }

  selectAllWeekdays(): void {
    this.notificationsValues.update((values: GetAlertsSettingsQueryResult) => ({
      ...values,
      specificWeekdays: this.weekDays
    }));
  }
}
